/**
 * Structured network field notes (topology, VLANs, SSIDs).
 *
 * Each note is one `documents` row keyed by s3_key = `<kind>/<location_id>`
 * (unique index idx_documents_s3_key_unique) with the form data in
 * `metadata`. The rows are internal (is_customer_visible = false) and are
 * owned by the customer of the trip they were captured on.
 *
 * Everything here is pure so the parsers can be unit-tested without a
 * browser or a Supabase client.
 */
import type { Json } from '@pazportal/supabase';

export type NetworkDocKind = 'topology' | 'vlans' | 'wireless';

export const NETWORK_DOC_KINDS: NetworkDocKind[] = ['topology', 'vlans', 'wireless'];

export const NETWORK_DOC_TITLES: Record<NetworkDocKind, string> = {
  topology: 'Network Topology',
  vlans: 'VLAN Configuration',
  wireless: 'Wireless Configuration',
};

export function networkDocKey(kind: NetworkDocKind, locationId: string): string {
  return `${kind}/${locationId}`;
}

// ─── Topology ────────────────────────────────────────────────────────────────

export interface TopologyForm {
  isp_provider: string;
  circuit_type: string;
  download_speed: string;
  upload_speed: string;
  circuit_id: string;
  static_ip_block: string;
  gateway: string;
  firewall_make: string;
  firewall_model: string;
  firewall_firmware: string;
  core_switch: string;
  idf_switch: string;
  poe_budget: string;
  poe_consumed: string;
  architecture_notes: string;
}

export const TOPOLOGY_FIELDS: { key: Exclude<keyof TopologyForm, 'architecture_notes'>; label: string; hint?: string; inputMode?: 'numeric' | 'decimal' }[] = [
  { key: 'isp_provider', label: 'ISP provider' },
  { key: 'circuit_type', label: 'Circuit type', hint: 'e.g. fiber, coax, LTE' },
  { key: 'download_speed', label: 'Download speed', hint: 'Mbps' },
  { key: 'upload_speed', label: 'Upload speed', hint: 'Mbps' },
  { key: 'circuit_id', label: 'Circuit ID' },
  { key: 'static_ip_block', label: 'Static IP block' },
  { key: 'gateway', label: 'Gateway' },
  { key: 'firewall_make', label: 'Firewall make' },
  { key: 'firewall_model', label: 'Firewall model' },
  { key: 'firewall_firmware', label: 'Firewall firmware' },
  { key: 'core_switch', label: 'Core switch' },
  { key: 'idf_switch', label: 'IDF switch' },
  { key: 'poe_budget', label: 'PoE budget', hint: 'W', inputMode: 'decimal' },
  { key: 'poe_consumed', label: 'PoE consumed', hint: 'W', inputMode: 'decimal' },
];

export const EMPTY_TOPOLOGY: TopologyForm = {
  isp_provider: '',
  circuit_type: '',
  download_speed: '',
  upload_speed: '',
  circuit_id: '',
  static_ip_block: '',
  gateway: '',
  firewall_make: '',
  firewall_model: '',
  firewall_firmware: '',
  core_switch: '',
  idf_switch: '',
  poe_budget: '',
  poe_consumed: '',
  architecture_notes: '',
};

// ─── VLAN / SSID rows ────────────────────────────────────────────────────────

export interface VlanRow {
  /** Local, render-only id. Not persisted. */
  id: string;
  vlan_id: string;
  name: string;
  subnet: string;
  gateway: string;
  purpose: string;
}

export interface SsidRow {
  id: string;
  ssid: string;
  security: string;
  vlan: string;
  aps: string;
  notes: string;
}

export const VLAN_FIELDS: { key: Exclude<keyof VlanRow, 'id'>; label: string; inputMode?: 'numeric' }[] = [
  { key: 'vlan_id', label: 'VLAN ID', inputMode: 'numeric' },
  { key: 'name', label: 'Name' },
  { key: 'subnet', label: 'Subnet' },
  { key: 'gateway', label: 'Gateway' },
  { key: 'purpose', label: 'Purpose' },
];

export const SSID_SECURITY_OPTIONS = ['WPA3-Enterprise', 'WPA3-Personal', 'WPA2-Enterprise', 'WPA2-PSK', 'Open'];

export const SSID_FIELDS: { key: Exclude<keyof SsidRow, 'id' | 'security'>; label: string }[] = [
  { key: 'ssid', label: 'SSID' },
  { key: 'vlan', label: 'VLAN' },
  { key: 'aps', label: 'Broadcasting APs' },
  { key: 'notes', label: 'Notes' },
];

let rowSeq = 0;
/** Unique within the page lifetime; ids are never written to the database. */
export function nextRowId(): string {
  rowSeq += 1;
  return `row-${rowSeq}`;
}

export function emptyVlanRow(): VlanRow {
  return { id: nextRowId(), vlan_id: '', name: '', subnet: '', gateway: '', purpose: '' };
}

export function emptySsidRow(): SsidRow {
  return { id: nextRowId(), ssid: '', security: SSID_SECURITY_OPTIONS[0], vlan: '', aps: '', notes: '' };
}

// ─── Parsing (metadata is untyped jsonb) ─────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

export function parseTopology(metadata: Json | null | undefined): TopologyForm {
  const record = asRecord(metadata) ?? {};
  const form: TopologyForm = { ...EMPTY_TOPOLOGY };
  for (const key of Object.keys(EMPTY_TOPOLOGY) as (keyof TopologyForm)[]) {
    form[key] = asText(record[key]);
  }
  return form;
}

export function parseVlans(metadata: Json | null | undefined): VlanRow[] {
  const list = asRecord(metadata)?.vlans;
  if (!Array.isArray(list)) return [];
  return list.flatMap((item) => {
    const r = asRecord(item);
    if (!r) return [];
    return [{
      id: nextRowId(),
      vlan_id: asText(r.vlan_id),
      name: asText(r.name),
      subnet: asText(r.subnet),
      gateway: asText(r.gateway),
      purpose: asText(r.purpose),
    }];
  });
}

export function parseSsids(metadata: Json | null | undefined): SsidRow[] {
  const list = asRecord(metadata)?.ssids;
  if (!Array.isArray(list)) return [];
  return list.flatMap((item) => {
    const r = asRecord(item);
    if (!r) return [];
    return [{
      id: nextRowId(),
      ssid: asText(r.ssid),
      security: asText(r.security) || SSID_SECURITY_OPTIONS[0],
      vlan: asText(r.vlan),
      aps: asText(r.aps),
      notes: asText(r.notes),
    }];
  });
}

// ─── Serialising ─────────────────────────────────────────────────────────────

function isBlankRow(row: object, ignore: string[] = []): boolean {
  return Object.entries(row as Record<string, unknown>).every(
    ([k, v]) => k === 'id' || ignore.includes(k) || (typeof v === 'string' ? v.trim() === '' : v == null),
  );
}

export function serializeTopology(form: TopologyForm): Json {
  const out: Record<string, string> = {};
  for (const key of Object.keys(EMPTY_TOPOLOGY) as (keyof TopologyForm)[]) out[key] = form[key].trim();
  return out;
}

/** Drops fully-empty rows and the local ids. */
export function serializeVlans(rows: VlanRow[]): Json {
  return {
    vlans: rows
      .filter((r) => !isBlankRow(r))
      .map(({ vlan_id, name, subnet, gateway, purpose }) => ({
        vlan_id: vlan_id.trim(),
        name: name.trim(),
        subnet: subnet.trim(),
        gateway: gateway.trim(),
        purpose: purpose.trim(),
      })),
  };
}

export function serializeSsids(rows: SsidRow[]): Json {
  return {
    ssids: rows
      .filter((r) => !isBlankRow(r, ['security']))
      .map(({ ssid, security, vlan, aps, notes }) => ({
        ssid: ssid.trim(),
        security,
        vlan: vlan.trim(),
        aps: aps.trim(),
        notes: notes.trim(),
      })),
  };
}
